// API client para /mi-cuenta (multi-pet self-service).
//
// El backend setea cookie `myl_customer_session` httpOnly al verificar el
// magic link. Todas las requests siguientes envían `credentials: 'include'`.

import type {
  DogFormData,
  AgeCategory,
  ActivityLevel,
  BodyCondition,
  DietType,
  Pathology,
  Protein,
  Gender,
} from '../types';
import { normalizeAge, normalizeBody, normalizeActivity, normalizeDiet } from '../utils/petParamNormalization';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '') || '/api/v1';

export interface AccountPet {
  id: string;
  name: string;
  breed_key: string | null;
  breed_display_name: string | null;
  weight_kg: number | null;
  age_months: number | null;
  age_category: string | null;
  body_condition: string | null;
  activity_level: string | null;
  gender: string | null;
  is_neutered: boolean | null;
  pathologies: string[];
  allergies: string[];
  diet_type: string | null;
  daily_kcal: number | null;
  daily_grams: number | null;
  selected_recipe_variant_ids: string[];
  selected_supplement_variant_ids: string[];
  status: 'active' | 'paused' | 'removed';
  variant_quantities: Record<string, number>;
}

export interface AccountSubscription {
  id: string;
  email: string;
  dog_name: string;
  status: string;
  subscription_status: string | null;
  platform: string;
  next_renewal_at: string | null;
  shopify_order_id: string | null;
  appstle_subscription_id: string | null;
  pets: AccountPet[];
  capabilities?: {
    provider: string;
    can_pause: boolean;
    can_resume: boolean;
    can_skip: boolean;
    can_add_pet: boolean;
    can_edit_pet_profile: boolean;
    can_edit_recipes: boolean;
    can_cancel_self_service: boolean;
    requires_support_for_cancellation: boolean;
  };
  created_at: string | null;
}

export interface AccountMeResponse {
  email: string;
  subscriptions: AccountSubscription[];
}

export interface AccountSubscriptionLiveDetail {
  ok: boolean;
  provider: 'appstle';
  subscription_id: string;
  contract_id: number;
  status: string | null;
  next_billing_date: string | null;
  billing_interval_count: number | null;
  lines: Array<{
    variant_id: string;
    quantity: number;
    current_price: number;
    line_total: number;
  }>;
  estimated_total_price: number;
}

export async function requestMagicLink(email: string): Promise<void> {
  const resp = await fetchWithTimeout(`${API_BASE}/account/request-magic-link`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  // 204 ó 5xx — siempre tratamos como "ok, mira tu email" para no revelar
  // qué emails están en BD. Solo lanzamos en errores claros (>=500).
  if (resp.status >= 500) {
    throw new Error('No pudimos enviar el email. Inténtalo más tarde.');
  }
}

export async function verifyMagicLink(token: string): Promise<{ email: string }> {
  const resp = await fetchWithTimeout(`${API_BASE}/account/verify-magic-link?token=${encodeURIComponent(token)}`, {
    credentials: 'include',
  });
  if (resp.status === 401) {
    throw new Error('El enlace ha caducado o es inválido. Pide uno nuevo.');
  }
  if (!resp.ok) {
    throw new Error('No pudimos validar el enlace. Inténtalo de nuevo.');
  }
  return resp.json();
}

export async function getAccountMe(): Promise<AccountMeResponse> {
  return accountFetch<AccountMeResponse>('/account/me', {}, 'No pudimos cargar tus datos.');
}

export async function logout(): Promise<void> {
  await fetch(`${API_BASE}/account/logout`, {
    method: 'POST',
    credentials: 'include',
  });
}

export async function getSubscriptionLiveDetail(subscriptionId: string): Promise<AccountSubscriptionLiveDetail> {
  return accountFetch<AccountSubscriptionLiveDetail>(
    `/account/subscriptions/${encodeURIComponent(subscriptionId)}/live`,
    {},
    'No pudimos cargar el detalle de la suscripción.',
  );
}

/** Error específico de no-auth para que el componente decida cómo recuperar
 *  (mostrar form de magic link). */
export class AccountUnauthorizedError extends Error {
  constructor() {
    super('No autenticado');
    this.name = 'AccountUnauthorizedError';
  }
}

/** Evento global que despacha accountFetch al recibir un 401. MiCuentaPage lo
 * escucha y vuelve al login, de modo que CUALQUIER panel (frecuencia, dirección,
 * pausar/saltar, recetas…) con sesión caducada redirige al login en vez de
 * mostrar "No autenticado" inline y dejar al cliente atascado (auditoría #2). */
export const ACCOUNT_UNAUTHORIZED_EVENT = 'myl:account-unauthorized';

/**
 * Wrapper común para llamadas autenticadas a `/account/*`.
 *
 * Centraliza el manejo de:
 *   - 401 → AccountUnauthorizedError (caller decide redirect a login)
 *   - !ok → intenta extraer `data.detail` del backend, sino fallback
 *   - 204 / body vacío → resuelve a undefined
 *
 * El helper NO añade `Content-Type` por defecto: si haces POST/PUT con body
 * JSON, pásalo en `init.headers`.
 */
/** Timeout de red para las llamadas de Mi Cuenta. Sin esto, una request colgada
 * dejaba el botón/spinner ("Pausando…", "Guardando…") infinito (auditoría #5). */
const ACCOUNT_FETCH_TIMEOUT_MS = 15000;

/**
 * `fetch` con timeout por AbortController. Si el caller pasa su propio signal,
 * lo respetamos sin añadir timeout; si no, abortamos a los 15s para no colgar la
 * UI. El AbortError se traduce a mensaje legible en AMBOS casos (Codex B7).
 *
 * Unifica el timeout de `accountFetch` Y de los fetch públicos (login por magic
 * link/OTP, preview, editor de catálogo) que antes podían colgar la UI sin
 * límite (auditoría loop frontend H2).
 */
async function fetchWithTimeout(
  url: string,
  init: RequestInit = {},
  timeoutMs: number = ACCOUNT_FETCH_TIMEOUT_MS,
): Promise<Response> {
  const ownController = init.signal ? null : new AbortController();
  const timeoutId = ownController ? setTimeout(() => ownController.abort(), timeoutMs) : undefined;
  try {
    return await fetch(url, {
      ...init,
      // signal al final para que gane: el propio si lo hay, si no el del timeout.
      signal: init.signal ?? ownController!.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('La conexión tardó demasiado. Revisa tu red e inténtalo de nuevo.');
    }
    throw err;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

/**
 * Despacha el evento global de sesión caducada (401). `MiCuentaPage` lo escucha
 * y vuelve al login. DEBE invocarse en TODO 401 del portal —no solo en
 * `accountFetch`— o un 401 al guardar/cargar deja al cliente atascado con un
 * "No autenticado" inline sin vía de recuperación (auditoría loop frontend H1).
 */
function dispatchUnauthorized(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(ACCOUNT_UNAUTHORIZED_EVENT));
  }
}

async function accountFetch<T>(
  path: string,
  init: RequestInit = {},
  fallbackError: string = 'Algo salió mal. Inténtalo de nuevo.',
): Promise<T> {
  const resp = await fetchWithTimeout(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
  });
  if (resp.status === 401) {
    dispatchUnauthorized();
    throw new AccountUnauthorizedError();
  }
  if (!resp.ok) {
    let detail = fallbackError;
    try {
      const data = await resp.json();
      if (data?.detail) detail = String(data.detail);
    } catch {
      // body wasn't JSON — keep fallback
    }
    throw new Error(detail);
  }
  // 204 No Content: válido, sin body que parsear.
  if (resp.status === 204) return undefined as T;
  // Para cualquier otro 2xx exigimos JSON parseable. Tragar este error
  // devolvería `undefined` con tipo `T`, lo que rompería al primer acceso
  // (`.subscriptions` en undefined) en el caller — mejor fallar explícito.
  return (await resp.json()) as T;
}

// ---------------------------------------------------------------------------
// Get pet data (para hidratación del update mode)
// ---------------------------------------------------------------------------

/**
 * Obtiene los datos de un pet por ID y los mapea al shape de DogFormData.
 * Usado por el update mode para pre-rellenar el formulario con los datos
 * guardados del perro cuando el usuario abre "Actualizar plan".
 */
export async function getPetData(petId: string): Promise<Partial<DogFormData>> {
  const resp = await fetchWithTimeout(`${API_BASE}/account/pets/${encodeURIComponent(petId)}`, {
    credentials: 'include',
  });
  if (resp.status === 401) {
    // H1: despachar el evento ANTES del throw — si no, una sesión caducada al
    // hidratar el update mode dejaba al cliente atascado sin redirect al login.
    dispatchUnauthorized();
    throw new AccountUnauthorizedError();
  }
  if (resp.status === 404) {
    throw new Error('La mascota no se encontró o no tienes acceso.');
  }
  if (!resp.ok) throw new Error('No pudimos cargar los datos de la mascota.');
  const pet: AccountPet = await resp.json();
  return mapPetToFormData(pet);
}

function mapPetToFormData(pet: AccountPet): Partial<DogFormData> {
  const result: Partial<DogFormData> = {};

  if (pet.name) result.nombre = pet.name;
  if (pet.breed_key) result.raza = pet.breed_key;
  if (pet.weight_kg !== null && pet.weight_kg !== undefined) result.peso = pet.weight_kg;
  if (pet.age_months !== null && pet.age_months !== undefined) {
    result.ageTotalMonths = pet.age_months;
    result.ageYears = Math.floor(pet.age_months / 12);
    result.ageMonths = pet.age_months % 12;
  }
  // Normalizar alias legacy en inglés (puppy/thin/moderate/mixed...) que aún
  // puedan vivir en la DB de pets — sin esto, el update-mode hidrataba el
  // funnel con valores que /calculate-plan rechaza (422) y, al guardar, se
  // re-escribía el dato sucio perpetuándolo.
  if (pet.age_category) result.ageCategory = normalizeAge(pet.age_category) as AgeCategory;
  if (pet.body_condition) result.bodyCondition = normalizeBody(pet.body_condition) as BodyCondition;
  if (pet.activity_level) result.activityLevel = normalizeActivity(pet.activity_level) as ActivityLevel;
  if (pet.gender) result.gender = pet.gender as Gender;
  if (pet.is_neutered !== null && pet.is_neutered !== undefined) result.isNeutered = pet.is_neutered;
  if (pet.pathologies && pet.pathologies.length > 0) {
    result.pathologies = pet.pathologies as Pathology[];
  }
  if (pet.allergies && pet.allergies.length > 0) {
    result.proteinAllergies = pet.allergies as Array<Protein | string>;
  }
  if (pet.diet_type) result.dietType = normalizeDiet(pet.diet_type) as DietType;
  if (pet.selected_recipe_variant_ids && pet.selected_recipe_variant_ids.length > 0) {
    result.selectedRecipes = pet.selected_recipe_variant_ids;
  }
  if (pet.selected_supplement_variant_ids && pet.selected_supplement_variant_ids.length > 0) {
    result.selectedSupplementVariantIds = pet.selected_supplement_variant_ids;
  }

  return result;
}

// ---------------------------------------------------------------------------
// Update pet plan (update mode — modifica datos del perro sin crear checkout)
// ---------------------------------------------------------------------------

export interface UpdatePetPayload {
  name?: string;
  breedKey?: string;
  breedDisplayName?: string;
  weightKg?: number;
  ageMonths?: number;
  ageCategory?: string;
  bodyCondition?: string;
  activityLevel?: string;
  gender?: string;
  isNeutered?: boolean;
  pathologies?: string[];
  allergies?: string[];
  dietType?: string;
  dailyKcal?: number;
  dailyGrams?: number;
  selectedRecipeVariantIds?: string[];
  selectedSupplementVariantIds?: string[];
  /** Distribución mensual (28 días): variantId → cantidad.
   *  Si se proporciona, el backend intenta actualizar las líneas del contract Appstle. */
  monthlyPlanDistribution?: Record<string, number>;
  /** Distribución del pack de iniciación: variantId → cantidad (opcional). */
  initiationPackDistribution?: Record<string, number>;
}

export interface UpdatePetResult {
  ok: boolean;
  pet: AccountPet;
  /** Presente en respuestas 200 y 207 cuando se envió monthlyPlanDistribution. */
  pet_updated?: boolean;
  /** true → Appstle actualizado; false → Appstle falló (207 degraded). */
  subscription_updated?: boolean;
  /** Mensaje de error de Appstle cuando subscription_updated es false. */
  subscription_error?: string;
}

export async function updatePetPlan(
  subscriptionId: string,
  petId: string,
  payload: UpdatePetPayload,
): Promise<UpdatePetResult> {
  const resp = await fetchWithTimeout(
    `${API_BASE}/account/subscriptions/${encodeURIComponent(subscriptionId)}/pets/${encodeURIComponent(petId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  if (resp.status === 401) {
    // H1: la escritura principal del portal (guardar recetas/parámetros). Sin
    // despachar el evento, una sesión caducada al GUARDAR dejaba al cliente
    // atascado con "No autenticado" inline en RecipesEditor/ParamsEditor/Step13.
    dispatchUnauthorized();
    throw new AccountUnauthorizedError();
  }
  if (resp.status === 404) throw new Error('La mascota o suscripción no se encontró.');
  // 207 Multi-Status: perfil guardado pero Appstle falló — degraded mode.
  // NO lanzamos error; devolvemos el resultado para que el caller decida.
  if (resp.status === 207) {
    return resp.json() as Promise<UpdatePetResult>;
  }
  if (!resp.ok) {
    let detail = 'No pudimos actualizar el plan.';
    try {
      const data = await resp.json();
      if (data?.detail) detail = String(data.detail);
    } catch {
      /* not JSON */
    }
    throw new Error(detail);
  }
  return resp.json() as Promise<UpdatePetResult>;
}

// ---------------------------------------------------------------------------
// Add pet
// ---------------------------------------------------------------------------

export interface AddPetLine {
  variantId: string;
  quantity: number;
  currentPrice: number;
}

export interface AddPetPayload {
  name: string;
  breedKey?: string;
  breedDisplayName?: string;
  weightKg?: number;
  ageMonths?: number;
  ageCategory?: string;
  bodyCondition?: string;
  activityLevel?: string;
  gender?: string;
  isNeutered?: boolean;
  pathologies?: string[];
  allergies?: string[];
  dietType?: string;
  dailyKcal?: number;
  dailyGrams?: number;
  lines: AddPetLine[];
}

// ---------------------------------------------------------------------------
// Pet parameter editor — preview cálculo + config del catálogo
// ---------------------------------------------------------------------------

export interface PetPreviewResult {
  daily_kcal: number;
  daily_grams: number;
}

export async function calculatePetPreview(params: {
  nombre: string;
  breedKey?: string | null;
  ageCategory: string;
  peso: number;
  bodyCondition: string;
  activityLevel: string;
  pathologies: string[];
  dietType: string;
  proteinAllergies: string[];
}): Promise<PetPreviewResult> {
  const resp = await fetchWithTimeout(`${API_BASE}/calculate-preview`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      animalType: 'perro',
      nombre: params.nombre || 'Mi perro',
      raza: params.breedKey ?? null,
      ageCategory: params.ageCategory,
      peso: params.peso,
      bodyCondition: params.bodyCondition,
      activityLevel: params.activityLevel,
      pathologies: params.pathologies,
      dietType: params.dietType,
      proteinAllergies: params.proteinAllergies,
    }),
  });
  if (!resp.ok) throw new Error('No pudimos calcular la previsualización.');
  return resp.json();
}

export interface EditorCatalogProduct {
  shopify_variant_id: string;
  kcal_per_100g: number | null;
  pack_size_g: number | null;
  price_eur: number;
  product_type?: string;
  display_name?: string;
  name?: string;
}

export interface EditorConfig {
  products: EditorCatalogProduct[];
  breed_database: { breeds: Record<string, { breed_size: string; display_name: string }> };
}

const EDITOR_CONFIG_TTL_MS = 5 * 60 * 1000; // 5 minutos

let _editorConfigCache: EditorConfig | null = null;
let _editorConfigCachedAt = 0;

export async function fetchEditorConfig(): Promise<EditorConfig> {
  const now = Date.now();
  if (_editorConfigCache && now - _editorConfigCachedAt < EDITOR_CONFIG_TTL_MS) {
    return _editorConfigCache;
  }
  const resp = await fetchWithTimeout(`${API_BASE}/app-config`);
  if (!resp.ok) throw new Error('No pudimos cargar la configuración del catálogo.');
  const data = await resp.json();
  // breed_database vive en su propio endpoint ahora (se sacó de /app-config).
  let breedDatabase = data.breed_database;
  if (!breedDatabase?.breeds || Object.keys(breedDatabase.breeds).length === 0) {
    try {
      const breedsResp = await fetchWithTimeout(`${API_BASE}/app-config/breeds`);
      if (breedsResp.ok) breedDatabase = (await breedsResp.json()).breed_database;
    } catch {
      /* nos quedamos con lo que trajera /app-config (o el fallback vacío de abajo) */
    }
  }
  const config: EditorConfig = {
    products: (data.products as EditorCatalogProduct[]) || [],
    breed_database: breedDatabase || { breeds: {} },
  };
  if (config.products.length > 0) {
    _editorConfigCache = config;
    _editorConfigCachedAt = now;
  }
  return config;
}

// ---------------------------------------------------------------------------
// Add pet
// ---------------------------------------------------------------------------

export async function skipNextOrder(
  subscriptionId: string,
): Promise<{ previous_billing_date: string; new_billing_date: string }> {
  return accountFetch<{ previous_billing_date: string; new_billing_date: string }>(
    `/account/subscriptions/${encodeURIComponent(subscriptionId)}/skip-next-order`,
    { method: 'POST' },
    'No pudimos saltar el pedido.',
  );
}

export async function pauseSubscription(subscriptionId: string): Promise<void> {
  await accountFetch<void>(
    `/account/subscriptions/${encodeURIComponent(subscriptionId)}/pause`,
    { method: 'POST' },
    'No pudimos pausar la suscripción. Inténtalo más tarde.',
  );
}

export async function resumeSubscription(subscriptionId: string): Promise<void> {
  await accountFetch<void>(
    `/account/subscriptions/${encodeURIComponent(subscriptionId)}/resume`,
    { method: 'POST' },
    'No pudimos reactivar la suscripción. Inténtalo más tarde.',
  );
}

export async function requestOtp(identifier: string, locale: string = 'es'): Promise<void> {
  const resp = await fetchWithTimeout(`${API_BASE}/account/request-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, locale }),
    credentials: 'include',
  });
  if (!resp.ok && resp.status !== 204) {
    throw new Error('No pudimos enviarte el SMS. Prueba con tu email.');
  }
}

export async function verifyOtp(identifier: string, code: string): Promise<void> {
  const resp = await fetchWithTimeout(`${API_BASE}/account/verify-otp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier, code }),
    credentials: 'include',
  });
  if (resp.status === 401) {
    throw new Error('Código incorrecto o caducado. Inténtalo de nuevo.');
  }
  if (!resp.ok) {
    throw new Error('Error al verificar el código.');
  }
}

export async function addPetToSubscription(
  subscriptionId: string,
  payload: AddPetPayload,
): Promise<{ ok: boolean; pet: AccountPet; next_renewal_at: string | null }> {
  return accountFetch<{ ok: boolean; pet: AccountPet; next_renewal_at: string | null }>(
    `/account/subscriptions/${encodeURIComponent(subscriptionId)}/pets`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
    'No pudimos añadir la mascota.',
  );
}

// ---------------------------------------------------------------------------
// Subscription details (shipping, frequency, delivery notes, order history)
// ---------------------------------------------------------------------------

export interface SubscriptionShippingAddress {
  first_name: string;
  last_name: string;
  address1: string;
  address2: string;
  city: string;
  province: string;
  zip: string;
  country: string;
  phone: string;
}

export interface SubscriptionPaymentMethod {
  brand: string;
  last4: string;
}

export interface SubscriptionDetails {
  shipping_address: SubscriptionShippingAddress | null;
  frequency_days: number;
  next_order_date: string | null;
  delivery_note: string | null;
  payment_method: SubscriptionPaymentMethod | null;
}

export interface OrderHistoryItem {
  id: string;
  created_at: string;
  total_eur: number;
  status: string;
  items: Array<{ name: string; qty: number; price_eur: number }>;
}

export async function fetchSubscriptionDetails(subId: string): Promise<SubscriptionDetails> {
  return accountFetch<SubscriptionDetails>(
    `/account/subscriptions/${encodeURIComponent(subId)}/details`,
    {},
    'No pudimos cargar los detalles de la suscripción.',
  );
}

export async function updateShippingAddress(subId: string, address: SubscriptionShippingAddress | null): Promise<void> {
  await accountFetch<void>(
    `/account/subscriptions/${encodeURIComponent(subId)}/shipping-address`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(address),
    },
    'No pudimos actualizar la dirección de envío.',
  );
}

export async function updateFrequency(
  subId: string,
  intervalDays: number,
): Promise<{ frequency_days: number; next_order_date: string }> {
  return accountFetch<{ frequency_days: number; next_order_date: string }>(
    `/account/subscriptions/${encodeURIComponent(subId)}/frequency`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ interval_days: intervalDays }),
    },
    'No pudimos actualizar la frecuencia.',
  );
}

export async function updateNextOrderDate(subId: string, date: string): Promise<void> {
  await accountFetch<void>(
    `/account/subscriptions/${encodeURIComponent(subId)}/next-order-date`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date }),
    },
    'No pudimos reprogramar el pedido.',
  );
}

export async function orderNow(subId: string): Promise<void> {
  await accountFetch<void>(
    `/account/subscriptions/${encodeURIComponent(subId)}/order-now`,
    { method: 'POST' },
    'No pudimos procesar el pedido.',
  );
}

export async function updateDeliveryNote(subId: string, note: string): Promise<void> {
  await accountFetch<void>(
    `/account/subscriptions/${encodeURIComponent(subId)}/delivery-note`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note }),
    },
    'No pudimos guardar la nota de entrega.',
  );
}

export async function fetchOrderHistory(): Promise<OrderHistoryItem[]> {
  return accountFetch<OrderHistoryItem[]>('/account/orders', {}, 'No pudimos cargar el historial de pedidos.');
}
